import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Zap, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Cpu,
  Network,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  Eye,
  BarChart3
} from 'lucide-react';

interface BeeMetric {
  id: string;
  name: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  uptime: string;
  lastActivity: string;
  tasksProcessed: number;
  averageResponseTime: number;
  memoryUsage: number;
  errorRate: number;
}

interface CircuitMetric {
  id: string;
  name: string;
  status: 'closed' | 'half-open' | 'open';
  successRate: number;
  failureCount: number;
  lastFailure?: string;
  responseTime: number;
}

const WealthOSOversight: React.FC = () => {
  const [beeMetrics, setBeeMetrics] = useState<BeeMetric[]>([]);
  const [circuitMetrics, setCircuitMetrics] = useState<CircuitMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      
      const mockBeeMetrics: BeeMetric[] = [
        {
          id: '1',
          name: 'FileRouterBee',
          status: 'healthy',
          uptime: '99.8%',
          lastActivity: '2 minutes ago',
          tasksProcessed: 1247,
          averageResponseTime: 145,
          memoryUsage: 68,
          errorRate: 0.2
        },
        {
          id: '2',
          name: 'PDFIngestBee',
          status: 'healthy',
          uptime: '99.5%',
          lastActivity: '5 minutes ago',
          tasksProcessed: 892,
          averageResponseTime: 2340,
          memoryUsage: 82,
          errorRate: 0.5
        },
        {
          id: '3',
          name: 'BookkeepingBee',
          status: 'warning',
          uptime: '97.2%',
          lastActivity: '1 hour ago',
          tasksProcessed: 456,
          averageResponseTime: 890,
          memoryUsage: 91,
          errorRate: 2.8
        },
        {
          id: '4',
          name: 'FormFillerBee',
          status: 'healthy',
          uptime: '99.9%',
          lastActivity: '30 seconds ago',
          tasksProcessed: 234,
          averageResponseTime: 567,
          memoryUsage: 45,
          errorRate: 0.1
        },
        {
          id: '5',
          name: 'AdvisorBee',
          status: 'error',
          uptime: '85.3%',
          lastActivity: '3 hours ago',
          tasksProcessed: 123,
          averageResponseTime: 1200,
          memoryUsage: 95,
          errorRate: 14.7
        }
      ];

      const mockCircuitMetrics: CircuitMetric[] = [
        {
          id: '1',
          name: 'BeeMemory Connection',
          status: 'closed',
          successRate: 99.8,
          failureCount: 2,
          responseTime: 45
        },
        {
          id: '2',
          name: 'Dropbox Webhook',
          status: 'closed',
          successRate: 98.5,
          failureCount: 15,
          responseTime: 120
        },
        {
          id: '3',
          name: 'Tax API Service',
          status: 'half-open',
          successRate: 92.1,
          failureCount: 79,
          lastFailure: '15 minutes ago',
          responseTime: 890
        },
        {
          id: '4',
          name: 'PDF Processing',
          status: 'closed',
          successRate: 97.3,
          failureCount: 27,
          responseTime: 2100
        }
      ];

      setBeeMetrics(mockBeeMetrics);
      setCircuitMetrics(mockCircuitMetrics);
      setLoading(false);
    };

    loadMetrics();
    
    if (autoRefresh) {
      const interval = setInterval(loadMetrics, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [selectedTimeRange, autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'closed':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'warning':
      case 'half-open':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'error':
      case 'open':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'offline':
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      case 'warning':
      case 'half-open':
        return <AlertTriangle className="w-4 h-4" />;
      case 'error':
      case 'open':
        return <AlertTriangle className="w-4 h-4" />;
      case 'offline':
        return <Clock className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading oversight metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-900 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <Activity className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white text-drop-shadow">System Oversight</h1>
              <p className="text-slate-400">Monitor Bee health and circuit breaker status</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-teal-500"
              />
              <label htmlFor="autoRefresh" className="text-sm text-slate-300">Auto-refresh</label>
            </div>
            
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="5m">Last 5 minutes</option>
              <option value="1h">Last hour</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
            </select>
            
            <button className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 font-medium transition-colors flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Active Bees</h3>
                <p className="text-2xl font-bold text-white">{beeMetrics.filter(b => b.status !== 'offline').length}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-green-400">All systems operational</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Tasks Processed</h3>
                <p className="text-2xl font-bold text-white">{beeMetrics.reduce((sum, bee) => sum + bee.tasksProcessed, 0).toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-blue-400 mr-1" />
              <span className="text-blue-400">+15% vs last hour</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Avg Response Time</h3>
                <p className="text-2xl font-bold text-white">{formatResponseTime(Math.round(beeMetrics.reduce((sum, bee) => sum + bee.averageResponseTime, 0) / beeMetrics.length))}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <TrendingDown className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-green-400">-8% improvement</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Error Rate</h3>
                <p className="text-2xl font-bold text-white">{(beeMetrics.reduce((sum, bee) => sum + bee.errorRate, 0) / beeMetrics.length).toFixed(1)}%</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <TrendingDown className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-green-400">Within acceptable range</span>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-teal-400" />
              Bee Health Status
            </h3>
            
            <div className="space-y-4">
              {beeMetrics.map((bee) => (
                <div key={bee.id} className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(bee.status)}`}>
                        {getStatusIcon(bee.status)}
                        {bee.status}
                      </span>
                      <h4 className="font-medium text-white">{bee.name}</h4>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Uptime</span>
                      <p className="font-medium text-white">{bee.uptime}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Tasks</span>
                      <p className="font-medium text-white">{bee.tasksProcessed.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Response Time</span>
                      <p className="font-medium text-white">{formatResponseTime(bee.averageResponseTime)}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Memory</span>
                      <p className="font-medium text-white">{bee.memoryUsage}%</p>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-400">Memory Usage</span>
                      <span className="text-slate-300">{bee.memoryUsage}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          bee.memoryUsage > 90 ? 'bg-red-400' : 
                          bee.memoryUsage > 75 ? 'bg-yellow-400' : 'bg-green-400'
                        }`}
                        style={{ width: `${bee.memoryUsage}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-400 mt-2">Last activity: {bee.lastActivity}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Network className="w-5 h-5 text-blue-400" />
              Circuit Breaker Status
            </h3>
            
            <div className="space-y-4">
              {circuitMetrics.map((circuit) => (
                <div key={circuit.id} className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(circuit.status)}`}>
                        {getStatusIcon(circuit.status)}
                        {circuit.status}
                      </span>
                      <h4 className="font-medium text-white">{circuit.name}</h4>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-slate-400">Success Rate</span>
                      <p className="font-medium text-white">{circuit.successRate}%</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Failures</span>
                      <p className="font-medium text-white">{circuit.failureCount}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Response Time</span>
                      <p className="font-medium text-white">{formatResponseTime(circuit.responseTime)}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Status</span>
                      <p className="font-medium text-white capitalize">{circuit.status.replace('-', ' ')}</p>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-400">Success Rate</span>
                      <span className="text-slate-300">{circuit.successRate}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          circuit.successRate > 95 ? 'bg-green-400' : 
                          circuit.successRate > 85 ? 'bg-yellow-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${circuit.successRate}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {circuit.lastFailure && (
                    <p className="text-xs text-slate-400">Last failure: {circuit.lastFailure}</p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WealthOSOversight;
